import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Products' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-gray-300 py-2.5 text-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-yellow-400">📍</span>
            <span className="font-medium">Factory: Amritsar, India</span>
          </div>
          <div className="hidden sm:flex gap-6 items-center">
            <a href="tel:+916239019768" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Phone size={14} />
              <span>+91 62390 19768</span>
            </a>
            <a href="mailto:rkkohli.sons@gmail.com" className="flex items-center gap-2 hover:text-yellow-400 transition-colors">
              <Mail size={14} />
              <span>rkkohli.sons@gmail.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/98 backdrop-blur-xl shadow-lg' 
            : 'bg-white/95 backdrop-blur-custom shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
                <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110 group-hover:rotate-3 duration-300">
                    <img
                        src="/logo.svg"
                        alt="Arkotherm Logo"
                        className="w-11 h-11 object-contain"
                    />
                </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-gray-900 tracking-tight">ARKOTHERM</span>
                <span className="text-xs text-gray-500 font-medium -mt-1">Industrial Furnaces</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 font-semibold text-sm transition-all duration-300 rounded-lg ${
                    isActive(link.path)
                      ? 'text-orange-600'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full"></span>
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="relative px-6 py-3 gradient-bg text-white font-bold rounded-full overflow-hidden group shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="header-request-quote-btn"
              >
                <span className="relative z-10">Request Quote</span>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2.5 text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-300"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 px-4 font-semibold rounded-lg transition-all duration-300 ${
                    isActive(link.path) 
                      ? 'text-orange-600 bg-orange-50' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full py-3.5 gradient-bg text-white font-bold rounded-full text-center mt-4 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Request Quote
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;